import { useEffect, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";
import {
  errorEnvelopeSchema,
  pathLabelOf,
  type BulkMoveItemResult,
  type Container,
} from "@egalregal/shared";
import { ApiError, requests } from "../../shared/api";
import { log } from "../../logger";
import { useAppStore } from "../../shared/store/app";
import { useT } from "../../shared/i18n";
import { useShell } from "../shell/useShell";
import { DestinationEntryList, destinationEntryChoices } from "../catalogs/DestinationEntryList";
import { DestinationParentPicker } from "../catalogs/DestinationParentPicker";

/** One selected item placement in the source container. */
export interface ItemBulkMoveRow {
  id: string;
  name: string;
}

/** What the dialog hands back after a committed item batch. */
export interface ItemBulkMoveOutcome {
  result: BulkMoveItemResult;
  /** Destination catalog entry, for scoping the Open link. */
  entryId: string;
  /** Destination container whose detail page the snackbar links to. */
  containerId: string;
  destinationLabel: string;
}

interface ItemBulkMoveDialogProps {
  open: boolean;
  /** The container whose placements are selected. Never offered as a target. */
  sourceContainerId: string;
  /** The entry holding the source; step one preselects it. */
  sourceEntryId: string;
  /**
   * Owner of the source home. Own entries are offered when this is undefined;
   * a shared source offers only writable entries from the same owner. Item
   * moves never cross homes.
   */
  sourceOwner?: string;
  items: readonly ItemBulkMoveRow[];
  onClose: () => void;
  onComplete: (outcome: ItemBulkMoveOutcome) => void;
}

type Step = "entry" | "parent";

interface MoveError {
  message: string;
  rows: string[];
}

/**
 * Destination flow for an item-placement batch (bulk-move 06, ADR-0040).
 *
 * Step one lists only writable entries in the source container's home and
 * preselects the current entry. Step two reuses the container tree: the source
 * is a drill-only `source` node so its children stay reachable but the source
 * cannot be chosen, and the target search never matches it. The batch is
 * all-or-nothing, so every failure keeps the dialog open and the selection
 * intact; {@link ItemBulkMoveOutcome} is reported only after a commit.
 */
export function ItemBulkMoveDialog({
  open,
  sourceContainerId,
  sourceEntryId,
  sourceOwner,
  items,
  onClose,
  onComplete,
}: ItemBulkMoveDialogProps) {
  const t = useT();
  const shell = useShell();
  const entries = useAppStore((s) => s.entries);
  const sharedWithMe = useAppStore((s) => s.sharedWithMe);
  const [step, setStep] = useState<Step>("entry");
  const [selectedId, setSelectedId] = useState<string | null>(sourceEntryId);
  const [destContainers, setDestContainers] = useState<Container[]>([]);
  const [parentId, setParentId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<MoveError | null>(null);

  // Item moves stay in the source home: own entries for an own source, the
  // sharing owner's writable entries for a shared one.
  const choices = destinationEntryChoices(entries, sharedWithMe).filter(
    (choice) => choice.owner === sourceOwner,
  );
  const chosen = choices.find((choice) => choice.id === selectedId);
  const entryLabel = chosen?.label ?? "";

  useEffect(() => {
    if (!open) return;
    setStep("entry");
    setSelectedId(sourceEntryId);
    setDestContainers([]);
    setParentId(null);
    setBusy(false);
    setError(null);
  }, [open, sourceEntryId, sourceContainerId]);

  const advance = async () => {
    if (!selectedId) return;
    setBusy(true);
    setError(null);
    try {
      setDestContainers(await requests.containers.list(selectedId));
      setParentId(null);
      setStep("parent");
    } catch (err) {
      log.warn(
        { err, entryId: selectedId.slice(0, 7) },
        "[item-bulk-move] destination entry load failed",
      );
      setError({ message: t("bulkDestinationLoadFailed"), rows: [] });
    } finally {
      setBusy(false);
    }
  };

  const commit = async () => {
    if (!selectedId || !parentId || parentId === sourceContainerId) return;
    setBusy(true);
    setError(null);
    try {
      const result = await requests.items.bulkMove(sourceContainerId, {
        itemIds: items.map((item) => item.id),
        targetContainerId: parentId,
      });
      const parent = destContainers.find((container) => container.id === parentId);
      onComplete({
        result,
        entryId: selectedId,
        containerId: parentId,
        destinationLabel: parent ? pathLabelOf(destContainers, parent.id) : entryLabel,
      });
    } catch (err) {
      // A pre-validation failure writes nothing; a storage failure leaves the
      // one placement file unmoved (ADR-0040). Both keep the dialog open.
      log.warn(
        { err, containerId: sourceContainerId.slice(0, 7) },
        "[item-bulk-move] batch rejected or failed",
      );
      setError(describeMoveError(err, items, t("itemBulkMoveFailed")));
    } finally {
      setBusy(false);
    }
  };

  // The source is reachable only to drill into its children; it is never a
  // selectable target. The field is scope-limited to the source's own entry.
  const drillOnly = new Set([sourceContainerId]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      fullScreen={shell === "phone"}
      data-testid="item-bulk-move-dialog"
    >
      <DialogTitle>{t("itemBulkMoveTitle")}</DialogTitle>

      {step === "entry" ? (
        <>
          <DialogContent data-testid="item-bulk-move-entry-step">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("itemBulkPickEntry")}
            </Typography>
            {error && <MoveErrorAlert error={error} />}
            <DestinationEntryList
              choices={choices}
              selectedId={selectedId}
              onSelect={(id) => {
                setError(null);
                setSelectedId(id);
              }}
              currentEntryId={sourceEntryId}
              sourceOwner={sourceOwner}
              testIdPrefix="item-bulk-entry"
            />
          </DialogContent>
          <DialogActions sx={DIALOG_ACTIONS_SX}>
            <Button onClick={onClose}>{t("cancel")}</Button>
            <Button
              variant="contained"
              disabled={!chosen || busy}
              data-testid="item-bulk-entry-confirm"
              onClick={() => void advance()}
            >
              {t("next")}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent data-testid="item-bulk-move-parent-step">
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {t("itemBulkPickContainer", { entry: entryLabel })}
            </Typography>
            {error && <MoveErrorAlert error={error} />}
            <DestinationParentPicker
              containers={destContainers}
              entryLabel={entryLabel}
              value={parentId}
              onChange={setParentId}
              drillOnlyIds={drillOnly}
              testIdPrefix="item-bulk-parent"
            />
          </DialogContent>
          <DialogActions sx={DIALOG_ACTIONS_SX}>
            <Button
              onClick={() => {
                setError(null);
                setStep("entry");
              }}
              disabled={busy}
              data-testid="item-bulk-parent-back"
            >
              {t("back")}
            </Button>
            <Button
              variant="contained"
              disabled={busy || parentId === null}
              data-testid="item-bulk-parent-confirm"
              onClick={() => void commit()}
            >
              {t("itemBulkMoveConfirm", { count: String(items.length) })}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
}

/**
 * Keep a failed batch in the dialog and name the offending placements. The
 * item batch never partially commits, so the same selection can simply be
 * corrected and retried.
 */
function MoveErrorAlert({ error }: { error: MoveError }) {
  return (
    <Alert severity="error" sx={{ mb: 1.5 }} data-testid="item-bulk-move-error">
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {error.message}
      </Typography>
      {error.rows.length > 0 && (
        <List dense disablePadding>
          {error.rows.map((row, index) => (
            <ListItem key={`${row}-${index}`} disablePadding>
              <ListItemText primary={row} />
            </ListItem>
          ))}
        </List>
      )}
    </Alert>
  );
}

/** Keep the phone's full-screen dialog actions above the home indicator. */
const DIALOG_ACTIONS_SX = { pb: "calc(8px + env(safe-area-inset-bottom))" };

/** Turn an API error envelope into a dialog message plus item-keyed reasons. */
function describeMoveError(
  err: unknown,
  items: readonly ItemBulkMoveRow[],
  fallback: string,
): MoveError {
  if (err instanceof ApiError) {
    const parsed = errorEnvelopeSchema.safeParse(err.body);
    if (parsed.success) {
      const details = parsed.data.error.details ?? [];
      return {
        message: parsed.data.error.message,
        rows: details.map((detail) => {
          const id = detail.path[1];
          const name = items.find((item) => item.id === id)?.name;
          return name ? `${name}: ${detail.message}` : detail.message;
        }),
      };
    }
  }
  return { message: err instanceof Error ? err.message : fallback, rows: [] };
}
